/**
 * Question Editor Component for Teachers
 * Create and manage pre-lab and discussion questions
 */

import { useState } from 'react';
import { Plus, Trash2, GripVertical, Edit2, Save, X } from 'lucide-react';

export type QuestionType = 'mcq' | 'true-false' | 'short-answer';

export interface PreLabQuestion {
    id: string;
    type: QuestionType;
    question: string;
    options?: string[];
    correctAnswer: string | number;
    points: number;
    explanation: string;
    order: number;
}

export interface DiscussionQuestion {
    id: string;
    question: string;
    guidanceNotes?: string;
    expectedKeyPoints?: string[];
    maxWords?: number;
    order: number;
}

interface PreLabQuestionEditorProps {
    questions: PreLabQuestion[];
    onChange: (questions: PreLabQuestion[]) => void;
}

export function PreLabQuestionEditor({ questions, onChange }: PreLabQuestionEditorProps) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<PreLabQuestion>>({});

    const startEdit = (question?: PreLabQuestion) => {
        if (question) {
            setEditForm(question);
            setEditingId(question.id);
        } else {
            // New question
            const newId = `q-${Date.now()}`;
            setEditForm({
                id: newId,
                type: 'mcq',
                question: '',
                options: ['', '', '', ''],
                correctAnswer: 0,
                points: 1,
                explanation: '',
                order: 0,
            });
            setEditingId(newId);
        }
    };

    const saveQuestion = () => {
        if (!editForm.question || editForm.question.trim() === '') return;

        const questionToSave = editForm as PreLabQuestion;
        const existing = questions.find(q => q.id === editingId);

        if (existing) {
            onChange(questions.map(q => q.id === editingId ? questionToSave : q).map((q, idx) => ({ ...q, order: idx })));
        } else {
            onChange([questionToSave, ...questions].map((q, idx) => ({ ...q, order: idx })));
        }

        setEditingId(null);
        setEditForm({});
    };

    const deleteQuestion = (id: string) => {
        if (confirm('Delete this question?')) {
            onChange(questions.filter(q => q.id !== id).map((q, idx) => ({ ...q, order: idx })));
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Pre-Lab Questions</h3>
                <button
                    onClick={() => startEdit()}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Question
                </button>
            </div>

            {/* Questions List */}
            <div className="space-y-3">
                {editingId && !questions.find(q => q.id === editingId) && (
                    <div className="bg-card border border-border rounded-lg p-4">
                        <QuestionEditForm
                            question={editForm as PreLabQuestion}
                            onChange={setEditForm}
                            onSave={saveQuestion}
                            onCancel={cancelEdit}
                        />
                    </div>
                )}
                {questions.map((question, index) => (
                    <div key={question.id} className="bg-card border border-border rounded-lg p-4">
                        {editingId === question.id ? (
                            <QuestionEditForm
                                question={editForm as PreLabQuestion}
                                onChange={setEditForm}
                                onSave={saveQuestion}
                                onCancel={cancelEdit}
                            />
                        ) : (
                            <div className="flex items-start gap-3">
                                <GripVertical className="w-5 h-5 text-muted-foreground mt-1 flex-shrink-0 cursor-move" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-indigo-600/20 text-indigo-600">
                                            Q{index + 1}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                                            {question.type.toUpperCase()}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {question.points} {question.points === 1 ? 'point' : 'points'}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-foreground mb-2">{question.question}</p>
                                    {question.options && (
                                        <div className="text-xs text-muted-foreground space-y-1">
                                            {question.options.map((opt, idx) => (
                                                <div key={idx} className={idx === question.correctAnswer ? 'font-bold text-green-600' : ''}>
                                                    {String.fromCharCode(65 + idx)}. {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => startEdit(question)}
                                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                                    >
                                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                    <button
                                        onClick={() => deleteQuestion(question.id)}
                                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function QuestionEditForm({
    question,
    onChange,
    onSave,
    onCancel,
}: {
    question: PreLabQuestion;
    onChange: (q: Partial<PreLabQuestion>) => void;
    onSave: () => void;
    onCancel: () => void;
}) {
    const updateField = (field: string, value: any) => {
        onChange({ ...question, [field]: value });
    };

    const updateOption = (index: number, value: string) => {
        const newOptions = [...(question.options || [])];
        newOptions[index] = value;
        onChange({ ...question, options: newOptions });
    };

    return (
        <div className="space-y-4">
            {/* Question Type */}
            <div>
                <label className="block text-sm font-medium mb-2">Question Type</label>
                <select
                    value={question.type}
                    onChange={(e) => updateField('type', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                >
                    <option value="mcq">Multiple Choice</option>
                    <option value="true-false">True/False</option>
                    <option value="short-answer">Short Answer</option>
                </select>
            </div>

            {/* Question Text */}
            <div>
                <label className="block text-sm font-medium mb-2">Question</label>
                <textarea
                    value={question.question}
                    onChange={(e) => updateField('question', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                    rows={3}
                    placeholder="Enter your question..."
                />
            </div>

            {/* Options (for MCQ and True/False) */}
            {(question.type === 'mcq' || question.type === 'true-false') && (
                <div>
                    <label className="block text-sm font-medium mb-2">Options</label>
                    {question.type === 'true-false' ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    checked={question.correctAnswer === 0}
                                    onChange={() => updateField('correctAnswer', 0)}
                                    className="w-4 h-4"
                                />
                                <span>True (Correct Answer)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    checked={question.correctAnswer === 1}
                                    onChange={() => updateField('correctAnswer', 1)}
                                    className="w-4 h-4"
                                />
                                <span>False (Correct Answer)</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {(question.options || []).map((opt, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        checked={question.correctAnswer === idx}
                                        onChange={() => updateField('correctAnswer', idx)}
                                        className="w-4 h-4"
                                    />
                                    <input
                                        type="text"
                                        value={opt}
                                        onChange={(e) => updateOption(idx, e.target.value)}
                                        placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                                        className="flex-1 px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Short Answer Correct Answer */}
            {question.type === 'short-answer' && (
                <div>
                    <label className="block text-sm font-medium mb-2">Correct Answer</label>
                    <input
                        type="text"
                        value={question.correctAnswer as string}
                        onChange={(e) => updateField('correctAnswer', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                        placeholder="Enter the correct answer..."
                    />
                </div>
            )}

            {/* Points */}
            <div>
                <label className="block text-sm font-medium mb-2">Points</label>
                <input
                    type="number"
                    value={question.points}
                    onChange={(e) => updateField('points', parseInt(e.target.value))}
                    className="w-32 px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                    min="1"
                />
            </div>

            {/* Explanation */}
            <div>
                <label className="block text-sm font-medium mb-2">Explanation</label>
                <textarea
                    value={question.explanation}
                    onChange={(e) => updateField('explanation', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                    rows={2}
                    placeholder="Explain the correct answer..."
                />
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-2"
                >
                    <X className="w-4 h-4" />
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    disabled={!question.question?.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-muted disabled:text-muted-foreground transition-colors flex items-center gap-2"
                >
                    <Save className="w-4 h-4" />
                    Save Question
                </button>
            </div>
        </div>
    );
}

interface DiscussionQuestionEditorProps {
    questions: DiscussionQuestion[];
    onChange: (questions: DiscussionQuestion[]) => void;
}

export function DiscussionQuestionEditor({ questions, onChange }: DiscussionQuestionEditorProps) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<DiscussionQuestion>>({});

    const startEdit = (question?: DiscussionQuestion) => {
        if (question) {
            setEditForm(question);
            setEditingId(question.id);
        } else {
            const newId = `dq-${Date.now()}`;
            setEditForm({
                id: newId,
                question: '',
                guidanceNotes: '',
                expectedKeyPoints: [],
                order: 0,
            });
            setEditingId(newId);
        }
    };

    const saveQuestion = () => {
        if (!editForm.question || editForm.question.trim() === '') return;

        const questionToSave = editForm as DiscussionQuestion;
        const existing = questions.find(q => q.id === editingId);

        if (existing) {
            onChange(questions.map(q => q.id === editingId ? questionToSave : q).map((q, idx) => ({ ...q, order: idx })));
        } else {
            onChange([questionToSave, ...questions].map((q, idx) => ({ ...q, order: idx })));
        }

        setEditingId(null);
        setEditForm({});
    };

    const deleteQuestion = (id: string) => {
        if (confirm('Delete this question?')) {
            onChange(questions.filter(q => q.id !== id).map((q, idx) => ({ ...q, order: idx })));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Discussion Questions</h3>
                <button
                    onClick={() => startEdit()}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Question
                </button>
            </div>

            <div className="space-y-3">
                {editingId && !questions.find(q => q.id === editingId) && (
                    <div className="bg-card border border-border rounded-lg p-4">
                        <DiscussionEditForm
                            question={editForm as DiscussionQuestion}
                            onChange={setEditForm}
                            onSave={saveQuestion}
                            onCancel={() => { setEditingId(null); setEditForm({}); }}
                        />
                    </div>
                )}
                {questions.map((question, index) => (
                    <div key={question.id} className="bg-card border border-border rounded-lg p-4">
                        {editingId === question.id ? (
                            <DiscussionEditForm
                                question={editForm as DiscussionQuestion}
                                onChange={setEditForm}
                                onSave={saveQuestion}
                                onCancel={() => { setEditingId(null); setEditForm({}); }}
                            />
                        ) : (
                            <div className="flex items-start gap-3">
                                <GripVertical className="w-5 h-5 text-muted-foreground mt-1 flex-shrink-0 cursor-move" />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-600/20 text-purple-600">
                                            Q{index + 1}
                                        </span>
                                        {question.maxWords && (
                                            <span className="text-xs text-muted-foreground">
                                                Max: {question.maxWords} words
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm font-medium text-foreground mb-2">{question.question}</p>
                                    {question.guidanceNotes && (
                                        <p className="text-xs text-blue-600 dark:text-blue-400 italic">
                                            Hint: {question.guidanceNotes}
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => startEdit(question)}
                                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                                    >
                                        <Edit2 className="w-4 h-4 text-muted-foreground" />
                                    </button>
                                    <button
                                        onClick={() => deleteQuestion(question.id)}
                                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function DiscussionEditForm({
    question,
    onChange,
    onSave,
    onCancel,
}: {
    question: DiscussionQuestion;
    onChange: (q: Partial<DiscussionQuestion>) => void;
    onSave: () => void;
    onCancel: () => void;
}) {
    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium mb-2">Question</label>
                <textarea
                    value={question.question}
                    onChange={(e) => onChange({ ...question, question: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                    rows={3}
                    placeholder="Enter discussion question..."
                />
            </div>

            <div>
                <label className="block text-sm font-medium mb-2">Guidance Notes (Hint for students)</label>
                <textarea
                    value={question.guidanceNotes}
                    onChange={(e) => onChange({ ...question, guidanceNotes: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                    rows={2}
                    placeholder="Optional hint or guidance..."
                />
            </div>

            <div>
                <label className="block text-sm font-medium mb-2">Max Words (Optional)</label>
                <input
                    type="number"
                    value={question.maxWords || ''}
                    onChange={(e) => onChange({ ...question, maxWords: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-32 px-3 py-2 rounded-lg bg-muted border border-border text-foreground"
                    placeholder="No limit"
                />
            </div>

            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-2"
                >
                    <X className="w-4 h-4" />
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    disabled={!question.question?.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-muted disabled:text-muted-foreground transition-colors flex items-center gap-2"
                >
                    <Save className="w-4 h-4" />
                    Save Question
                </button>
            </div>
        </div>
    );
}

